import { createContext, useContext, useEffect, useState } from "react";
import { getPublicSettings } from "../services/settingsService";

// The single source of truth for show_prices_to_guest, fetched ONCE here
// instead of separately by every page that needs it. Per the backend
// audit: there is no distinct "logged-in customer" price-visibility rule
// anywhere in FastAPI — GET /products returns the same `price` field
// regardless of auth state, and no second settings flag exists for
// customers. This single flag is therefore applied uniformly to guest
// AND logged-in customer, which is a deliberate documented decision, not
// an invented business rule (see the Phase-J final report).
const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [showPrices, setShowPrices] = useState(true); // optimistic default while loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPublicSettings()
      .then((data) => {
        if (cancelled) return;
        setShowPrices(Boolean(data?.show_prices_to_guest));
      })
      .catch(() => {
        // Network/backend error: fail safe by hiding prices rather than
        // risking showing them when we couldn't confirm the real setting.
        if (!cancelled) setShowPrices(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SettingsContext.Provider value={{ showPrices, settingsLoading: loading }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within SettingsProvider");
  return ctx;
}
